import type React from "react"
import { useMemo } from "react"

import { useMetricsList } from "@/metrics/catalog"
import { buildMetricRecordInstancesById } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"

import {
  MetricErrorStatus,
  type MetricErrorStatusType,
} from "@/types/api/MetricOutput"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import type {
  BucketClickData,
  DashboardWidgetConfig,
  DataSource,
} from "../widgets/types/core"
import type { MetricValue } from "../widgets/utils/chartShared"

interface RawDataPoint {
  value: number
  createdAt: string
  runId: string
  simulationOutputId: string
  metricId: string
  explanation: string
  explanationLoaded: boolean
  startTime: string
  endTime: string
  errorMessage: string | null
  errorStatus: string | null
  groundTruthValue: number
  groundTruthExplanation: string
  simulationOutputCreatedAt?: string
  runCreatedAt?: string
  customerMetadata?: Record<string, unknown>
}
interface UseRunDetailsDataParams {
  selectedPoint: BucketClickData | null
  metricId: string
  isMonitoring: boolean
  dataSource?: DataSource
  widgetConfig?: DashboardWidgetConfig
}

interface ProcessedTableRow {
  simulationOutputId: string
  runId: string
  status: string
  createdAt: Date
  createdAtString: string
  customerMetadata: Record<string, unknown>
  rawMetricValue: number
  metricId: string
  [metricDisplayName: string]: Date | Record<string, unknown> | React.ReactNode
}

interface UseRunDetailsDataReturn {
  // Raw data
  rawDataPoints: RawDataPoint[]
  metricRecordInstancesMap: Record<string, MetricRecord>

  // Processed data
  processedRows: ProcessedTableRow[]
  columns: Array<{
    key: string
    label: string
    sortable: boolean
  }>

  // Loading states
  isLoading: boolean
  hasError: boolean
  hasRequiredData: boolean
}

const COMPLETED_RUN_STATUS = "COMPLETED" as const

type BadgeMetricValue = Pick<
  RawDataPoint,
  "value" | "simulationOutputId" | "metricId" | "errorMessage" | "errorStatus"
>

function asMetricErrorStatus(
  value: string | null | undefined,
): MetricErrorStatusType | null {
  switch (value) {
    case MetricErrorStatus.IN_QUEUE:
    case MetricErrorStatus.IN_PROGRESS:
    case MetricErrorStatus.COMPLETED:
    case MetricErrorStatus.FAILED:
    case MetricErrorStatus.SKIPPED:
      return value
    case undefined:
    case null:
    default:
      return null
  }
}

export function buildDashboardBadgeMetricOutput(
  metricValue: BadgeMetricValue,
): RunMetricOutput {
  const errorStatus =
    asMetricErrorStatus(metricValue.errorStatus) ?? MetricErrorStatus.COMPLETED

  return {
    simulation_output_id: metricValue.simulationOutputId || "",
    metric_id: metricValue.metricId || "",
    output_type: "float",
    float_value: metricValue.value || 0,
    string_value: null,
    set_value: null,
    result_json: {},
    subvalues_by_timestamp: [],
    error_message: metricValue.errorMessage || null,
    error_status: errorStatus,
  }
}

export const useRunDetailsData = ({
  selectedPoint,
  metricId,
}: UseRunDetailsDataParams): UseRunDetailsDataReturn => {
  // Data queries for metric records only
  const { data: metricRecords, isLoading: isLoadingMetricRecords } =
    useMetricsList()

  // Extract and filter raw data points
  const rawDataPoints = useMemo(() => {
    // First priority: Use rawDataPoints from the clicked chart point (already filtered)
    if (
      selectedPoint?.rawDataPoints &&
      selectedPoint.rawDataPoints.length > 0
    ) {
      // Convert MetricValue objects to RawDataPoint format for compatibility
      return (
        selectedPoint.rawDataPoints as Array<MetricValue | RawDataPoint>
      ).map((point: MetricValue | RawDataPoint): RawDataPoint => {
        // If it's already a RawDataPoint, return as-is
        if ("explanation" in point && "explanationLoaded" in point) {
          return point as RawDataPoint
        }

        // Convert MetricValue to RawDataPoint format
        const metricValuePoint = point
        return {
          value: metricValuePoint.value,
          createdAt: metricValuePoint.createdAt,
          simulationOutputCreatedAt:
            metricValuePoint.simulationOutputCreatedAt ||
            metricValuePoint.createdAt,
          runCreatedAt:
            metricValuePoint.runCreatedAt || metricValuePoint.createdAt,
          customerMetadata: metricValuePoint.customerMetadata || {},
          runId: metricValuePoint.runId || "",
          simulationOutputId: metricValuePoint.simulationOutputId || "",
          metricId: metricValuePoint.metricId,
          explanation: metricValuePoint.explanation || "",
          explanationLoaded: true,
          startTime: "",
          endTime: "",
          errorMessage: null,
          errorStatus: COMPLETED_RUN_STATUS,
          groundTruthValue: metricValuePoint.value,
          groundTruthExplanation: metricValuePoint.explanation || "",
        }
      })
    }

    // No data available
    return []
  }, [selectedPoint])

  // Create metric records map
  const metricRecordInstancesMap = useMemo(
    () => buildMetricRecordInstancesById(metricRecords),
    [metricRecords],
  )

  // Process raw data points into table rows
  const processedRows = useMemo(() => {
    return rawDataPoints
      .map((metricValue): ProcessedTableRow | null => {
        if (!metricValue) {
          return null
        }

        const metricRecord = metricRecordInstancesMap[metricValue.metricId]
        if (!metricRecord) {
          return null
        }

        const row: ProcessedTableRow = {
          simulationOutputId: metricValue.simulationOutputId,
          runId: metricValue.runId,
          status: metricValue.errorStatus || COMPLETED_RUN_STATUS,
          createdAt: new Date(
            metricValue.simulationOutputCreatedAt || metricValue.createdAt,
          ),
          createdAtString: new Date(
            metricValue.simulationOutputCreatedAt || metricValue.createdAt,
          ).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
          customerMetadata: metricValue.customerMetadata || {},
          rawMetricValue: metricValue.value,
          metricId: metricValue.metricId,
        }

        // Add the metric value under the metric display name for dynamic column

        row[metricRecord.metric_display_name] = metricRecord.renderBadge(
          buildDashboardBadgeMetricOutput(metricValue),
        )

        return row
      })
      .filter((row): row is ProcessedTableRow => row !== null)
  }, [rawDataPoints, metricRecordInstancesMap])

  // Generate table columns
  const columns = useMemo(() => {
    const baseColumns = [
      { key: "simulationOutputId", label: "Simulation ID", sortable: false },
      { key: "createdAt", label: "Created At", sortable: true },
    ]

    // Add selected metric column
    if (metricId && metricRecordInstancesMap[metricId]) {
      const metricRecord = metricRecordInstancesMap[metricId]
      baseColumns.push({
        key: metricRecord.metric_display_name,
        label: metricRecord.metric_display_name,
        sortable: true,
      })
    }

    baseColumns.push({
      key: "customerMetadata",
      label: "Metadata",
      sortable: false,
    })

    return baseColumns
  }, [metricId, metricRecordInstancesMap])

  // Compute loading and error states
  const isLoading = isLoadingMetricRecords
  const hasError = false

  const hasRequiredData =
    rawDataPoints.length > 0 && Object.keys(metricRecordInstancesMap).length > 0

  return {
    rawDataPoints,
    metricRecordInstancesMap,
    processedRows,
    columns,
    isLoading,
    hasError,
    hasRequiredData,
  }
}
